// Pure WSLAM temporal helpers. They deliberately derive coverage only from the
// timestamps actually advertised by the loaded product catalog.

export function toWslamTimeMs(value) {
    if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string' || !value.trim()) return null;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
}

export function wslamStepTimeMs(step) {
    const value = [step?.valid_time, step?.datetime, step?.time, step?.key]
        .find(candidate => candidate !== null && candidate !== undefined && candidate !== '');
    return toWslamTimeMs(value);
}

export function normalizeWslamSteps(steps, timeOf = wslamStepTimeMs) {
    if (!Array.isArray(steps)) return [];

    const sorted = steps
        .map((step, index) => ({ step, index, timeMs: toWslamTimeMs(timeOf(step)) }))
        .filter(entry => Number.isFinite(entry.timeMs))
        .sort((left, right) => left.timeMs - right.timeMs || left.index - right.index);

    // Duplicate valid times do not expand coverage and must not create a zero
    // cadence. Keep the first catalog item so the returned index remains stable.
    return sorted.filter((entry, index) => index === 0 || entry.timeMs !== sorted[index - 1].timeMs);
}

export function getWslamTemporalRange(steps, timeOf = wslamStepTimeMs) {
    const normalized = normalizeWslamSteps(steps, timeOf);
    if (!normalized.length) {
        return {
            available: false,
            startMs: null,
            endMs: null,
            firstMs: null,
            lastMs: null,
            stepCount: 0,
            timestamps: [],
            firstCadenceMs: null,
            lastCadenceMs: null,
            toleranceMs: 0,
        };
    }

    const timestamps = normalized.map(entry => entry.timeMs);
    const firstMs = timestamps[0];
    const lastMs = timestamps[timestamps.length - 1];
    const firstCadenceMs = timestamps.length > 1 ? timestamps[1] - timestamps[0] : null;
    const lastCadenceMs = timestamps.length > 1
        ? timestamps[timestamps.length - 1] - timestamps[timestamps.length - 2]
        : null;
    const toleranceMs = timestamps.length > 1
        ? Math.max(...timestamps.slice(1).map((time, index) => time - timestamps[index])) / 2
        : 0;

    // A catalog step represents its Voronoi interval: half the observed cadence
    // before the first timestamp and half after the last. A lone step has no
    // defensible cadence, therefore it is available only at its exact instant.
    return {
        available: true,
        startMs: firstCadenceMs === null ? firstMs : firstMs - firstCadenceMs / 2,
        endMs: lastCadenceMs === null ? lastMs : lastMs + lastCadenceMs / 2,
        firstMs,
        lastMs,
        stepCount: timestamps.length,
        timestamps,
        firstCadenceMs,
        lastCadenceMs,
        toleranceMs,
    };
}

export function selectWslamStepAtTime(steps, target, timeOf = wslamStepTimeMs) {
    const targetMs = toWslamTimeMs(target);
    if (!Number.isFinite(targetMs)) return null;

    const normalized = normalizeWslamSteps(steps, timeOf);
    if (!normalized.length) return null;

    const coverage = getWslamTemporalRange(steps, timeOf);
    if (targetMs < coverage.startMs || targetMs > coverage.endMs) return null;

    let best = normalized[0];
    let bestDistance = Math.abs(best.timeMs - targetMs);
    for (let index = 1; index < normalized.length; index += 1) {
        const candidate = normalized[index];
        const distance = Math.abs(candidate.timeMs - targetMs);
        // A midpoint tie intentionally resolves to the earlier step.
        if (distance < bestDistance) {
            best = candidate;
            bestDistance = distance;
        }
    }

    return {
        step: best.step,
        index: best.index,
        timeMs: best.timeMs,
        targetMs,
        exact: best.timeMs === targetMs,
        distanceMs: bestDistance,
    };
}
